'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@workspace/ui/lib';
import * as React from 'react';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

// Context to share toggle function and refs between Tooltip, TooltipTrigger, and TooltipContent
interface TooltipContextValue {
  open: boolean;
  onToggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

function getTapElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Text) return target.parentElement;
  return null;
}

function findAnchorRelatedToTrigger(
  tapElement: Element | null,
  triggerEl: Element | null,
  clientX: number,
  clientY: number
): HTMLAnchorElement | null {
  let anchorEl = tapElement?.closest('a') ?? null;
  if (anchorEl && triggerEl) {
    const linksTrigger = triggerEl.contains(anchorEl) || anchorEl.contains(triggerEl);
    if (!linksTrigger) anchorEl = null;
  }

  if (!anchorEl && triggerEl && tapElement && triggerEl.contains(tapElement)) {
    const stack = document.elementsFromPoint(clientX, clientY);
    anchorEl =
      stack.find(
        (el): el is HTMLAnchorElement =>
          el instanceof HTMLAnchorElement && (triggerEl.contains(el) || el.contains(triggerEl))
      ) ?? null;
  }

  return anchorEl;
}

/**
 * Touch-friendly Tooltip that works on both desktop (hover) and mobile (tap).
 * On touch devices, tapping the trigger toggles the tooltip open/closed.
 * Tapping outside the tooltip closes it.
 */
function Tooltip({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = isControlled ? controlledOnOpenChange : setUncontrolledOpen;

  const onToggle = React.useCallback(() => {
    setOpen?.(!open);
  }, [open, setOpen]);

  // Close tooltip when clicking outside on touch devices
  React.useEffect(() => {
    if (!open) return;

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const triggerEl = triggerRef.current;
      const contentEl = contentRef.current;

      // Don't close if clicking on trigger or content
      if (triggerEl?.contains(target) || contentEl?.contains(target)) {
        return;
      }

      setOpen?.(false);
    };

    // Use a small delay to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [open, setOpen]);

  const contextValue = React.useMemo(
    () => ({ open, onToggle, triggerRef, contentRef }),
    [open, onToggle]
  );

  return (
    <TooltipProvider>
      <TooltipContext.Provider value={contextValue}>
        <TooltipPrimitive.Root data-slot="tooltip" open={open} onOpenChange={setOpen} {...props}>
          {children}
        </TooltipPrimitive.Root>
      </TooltipContext.Provider>
    </TooltipProvider>
  );
}

function TooltipTrigger({
  onClick,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const context = React.useContext(TooltipContext);
  const internalRef = React.useRef<HTMLButtonElement>(null);

  // Sync internal ref with context ref
  React.useEffect(() => {
    if (context?.triggerRef) {
      context.triggerRef.current = internalRef.current;
    }
  });

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Check if this is a touch device
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const tapElement = getTapElement(e.target);
      const triggerEl = internalRef.current;
      const anchorEl = findAnchorRelatedToTrigger(tapElement, triggerEl, e.clientX, e.clientY);
      const href = anchorEl?.getAttribute('href') ?? null;
      const isActivatableLink = Boolean(href && href !== '' && href !== '#');

      // Nested triggers inside Next.js <Link> must not call preventDefault on tap — that blocks
      // client-side navigation when the hit target is the label (e.g. TruncateWithTooltip).
      // Do not preventDefault on other nested triggers either — it blocks parent buttons
      // such as PopoverTrigger when the tooltip wraps truncated text.
      if (isTouchDevice && context && !isActivatableLink) {
        context.onToggle();
      }

      onClick?.(e);
    },
    [onClick, context]
  );

  return (
    <TooltipPrimitive.Trigger
      ref={internalRef}
      data-slot="tooltip-trigger"
      onClick={handleClick}
      {...props}
    />
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const context = React.useContext(TooltipContext);
  const internalRef = React.useRef<HTMLDivElement>(null);

  // Sync internal ref with context ref
  React.useEffect(() => {
    if (context?.contentRef) {
      context.contentRef.current = internalRef.current;
    }
  });

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={internalRef}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'border-grey-50 bg-neutral-100 text-grey-700 z-[1000] flex max-w-[287px] flex-col justify-start rounded-[20px] border p-6 text-sm leading-5 font-normal shadow-[0px_429px_120px_0px_rgba(42,43,65,0.00),0px_17px_38px_0px_rgba(42,43,65,0.05)]',
          'dark:bg-grey-900 dark:border-grey-700 dark:text-neutral-100 dark:shadow-[0px_17px_38px_0px_rgba(0,0,0,0.35)]',
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
